<template>
  <div>
    <on-input class="card-info-input"
      placeholder="1234 1234 1234 1234"
      :maxLength="23"
      inputmode="numeric"
      v-model="formCardNum"
      autocomplete="cc-number"
      @blur="cardNumberBlur"
      @focus="cardNumberFocus"
      @change="cardNumberChange"
      :error="(isFullValidation && cardNumRequired) || !validator.cardNum">
      <div slot="suffix">
        <!-- 卡种滚动效果，没有值的时候默认显示 -->
        <div class="card-info-suffix" v-show="!formCardNum">
          <div class="card-info-suffix-content" v-if="cardTypeInfo?.length">
            <img v-for="(item) in cardTypeInfo.slice(0, 3)" :key="item.logoPath" :src="item.logoPath" alt=""
              class="fixed-item" />
            <div class="fade-carousel" v-show="cardTypeInfo.length > 3">
              <img v-for="(item, index) in cardTypeInfo.slice(3)" :key="item.logoPath" :src="item.logoPath"
                class="fade-item" :class="{ active: index === activeIndex }" alt="" />
            </div>
          </div>
        </div>

        <div class="card-info-suffix-card" v-show="formCardNum">
          <img :src="card.logoPath" alt="" class="fixed-item" />
        </div>
      </div>
    </on-input>
    <div class="card-info-under-group">
      <!-- 有效期 -->
      <on-input class="effectiveDate-input"
        :maxLength="7"
        placeholder="MM / YY"
        autocomplete="cc-exp"
        inputmode="numeric"
        v-model="formEffectiveDate"
        @input.native="effectiveDateChangeHandler"
        @blur="effectiveDateBlur"
        @focus="effectiveDateFocus"
        @change="effectiveDateChange"
        :error="(isFullValidation && effectiveDateRequired) || !validator.effectiveDate">
      </on-input>

      <!-- cvv -->
      <on-input class="cvv-input"
        :maxLength="4"
        placeholder="CVC"
        autocomplete="cc-csc"
        v-model="formCvv"
        inputmode="numeric"
        @input.native="cvvChangeHandler"
        @blur="cvvBlur"
        @focus="cvvFocus"
        @change="cvvChange"
        :error="(isFullValidation && cvvRequired) || !validator.cvv">
        <div slot="suffix">
          <div class="img-cvv-box">
            <img class="icon-cvv" :src="require('@/assets/images/icon-cvv.png')" />
          </div>
        </div>
      </on-input>

    </div>

    <!-- 添加错误信息显示区域 -->
    <transition name="slide-bottom" appear>
      <div v-if="errorMsg" class="field-error bottom-left">{{ errorMsg }}</div>
    </transition>
  </div>
</template>

<script>
import { getCard } from '@/utils/validator';

import { cardNumberRules, effectiveDateRules, cvvRules } from './rules';

export default {
  name: 'card-information',
  props: {
    rules: {
      type: [Object, Array], // 修改类型定义，允许Array类型
      default: () => ({}),
    },
    value: {
      type: String,
      default: '',
    },
    cardNum: {
      type: String,
      default: '',
    },
    effectiveDate: {
      type: String,
      default: '',
    },
    cvv: {
      type: String,
      default: '',
    },
    cardTypeInfo: {
      type: Array,
      default: () => [],
    },
    // 添加验证模式属性，与动态表单组件保持一致
    validationMode: {
      type: String,
      default: 'format-only',
      validator: value => ['format-only', 'full'].includes(value),
    },
    // 添加外部错误状态，用于处理动态表单传递的错误状态
    error: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activeIndex: 0,
      card: { logoPath: require('@/assets/images/cards/Default.png') },
      formData: {
        cardNum: this.cardNum || this.value,
        effectiveDate: this.effectiveDate,
        cvv: this.cvv,
      },
      // 格式校验结果
      validator: {
        cardNum: true,
        effectiveDate: true,
        cvv: true,
      },
      errorMsg: '',
      // 内部验证模式，与动态表单保持一致
      internalValidationMode: this.validationMode,
    };
  },
  computed: {
    formCardNum: {
      get() {
        return this.cardNum || this.value;
      },
      set(newValue) {
        this.$emit('update:cardNum', newValue);
        this.$emit('input', newValue); // 添加对input事件的支持
      },
    },
    formEffectiveDate: {
      get() {
        return this.effectiveDate;
      },
      set(newValue) {
        this.$emit('update:effectiveDate', newValue);
      },
    },
    formCvv: {

      get() {
        return this.cvv;
      },
      set(newValue) {
        this.$emit('update:cvv', newValue);
      },
    },

    /**
     * 配置了必填且值为空才走必填校验
     *
     * 这里先确认配置是否有，再确认当前是否要校验（目前的逻辑是，不点pay按钮就不走必填校验）
     */
    // 卡号必填
    cardNumRequired() {
      // 处理数组类型的验证规则
      if (Array.isArray(this.rules)) {
        return this.rules.includes('required') && !this.formCardNum;
      }
      // 原有的对象类型验证规则处理
      return this.rules.cardNum?.some(item => item.name === 'required' && item.required) && !this.formCardNum;
    },

    // 有效期必填
    effectiveDateRequired() {
      if (Array.isArray(this.rules)) {
        return this.rules.includes('required') && !this.formEffectiveDate;
      }
      return (this.rules.effectiveDate?.some(item => item.name === 'required' && item.required) && !this.formEffectiveDate);
    },

    // cvv必填
    cvvRequired() {
      if (Array.isArray(this.rules)) {
        return this.rules.includes('required') && !this.formCvv;
      }
      return (this.rules.cvv?.some(item => item.name === 'required' && !!item.required) && !this.formCvv);
    },

    // 整体必填校验：只有当三个字段都有值时才通过
    allFieldsRequired() {
      // 检查是否启用必填校验
      const hasRequired = Array.isArray(this.rules)
        ? this.rules.includes('required')
        : (this.rules.cardNum?.some(item => item.name === 'required' && item.required) ||
           this.rules.effectiveDate?.some(item => item.name === 'required' && item.required) ||
           this.rules.cvv?.some(item => item.name === 'required' && item.required));

      // 如果启用了必填校验，检查三个字段是否都有值
      return hasRequired && (!this.formCardNum || !this.formEffectiveDate || !this.formCvv);
    },

    // 是否正在进行完整验证（包括必填）
    isFullValidation() {
      return this.internalValidationMode === 'full';
    },
  },
  mounted() {
    this.startCarousel();
  },
  methods: {
    cardNumberRules,
    effectiveDateRules,
    cvvRules,
    startCarousel() {
      setInterval(() => {
        this.activeIndex = (this.activeIndex + 1) % this.cardTypeInfo.slice(3).length;
      }, 2000);
    },

    cardNumberBlur() {
      this.validate();
      this.$emit('cardNumberBlur', this.formCardNum);
      this.$emit('blur', { name: 'cardNum', value: this.formCardNum }); // 添加对blur事件的支持
    },

    effectiveDateBlur() {
      this.validate();
      this.$emit('effectiveDateBlur', this.formEffectiveDate);
      // 始终使用cardNum作为错误的主键，保持一致性
      this.$emit('blur', { name: 'cardNum', value: this.formEffectiveDate });
    },

    cvvBlur() {
      this.validate();
      this.$emit('cvvBlur', this.formCvv);
      // 始终使用cardNum作为错误的主键，保持一致性
      this.$emit('blur', { name: 'cardNum', value: this.formCvv });
    },

    cardNumberFocus() {
      this.$emit('inputFocus', {
        name: 'cardNum',
        value: this.formCardNum,
      });
      this.$emit('focus', { name: 'cardNum', value: this.formCardNum }); // 添加对focus事件的支持

      // 移除校验逻辑，不在focus时触发校验
    },

    effectiveDateFocus() {
      this.$emit('inputFocus', {
        name: 'effectiveDate',
        value: this.formEffectiveDate,
      });
      this.$emit('focus', { name: 'effectiveDate', value: this.formEffectiveDate });

      // 移除校验逻辑，不在focus时触发校验
    },

    cvvFocus() {
      this.$emit('inputFocus', {
        name: 'cvv',
        value: this.formCvv,
      });
      this.$emit('focus', { name: 'cvv', value: this.formCvv });

      // 移除校验逻辑，不在focus时触发校验
    },

    cardNumberChange() {
      this.$emit('inputChange', {
        name: 'cardNum',
        value: this.formCardNum,
      });
      this.$emit('change', { name: 'cardNum', value: this.formCardNum }); // 添加对change事件的支持
    },

    effectiveDateChange() {
      this.$emit('inputChange', {
        name: 'effectiveDate',
        value: this.formEffectiveDate,
      });
      this.$emit('change', { name: 'effectiveDate', value: this.formEffectiveDate });
    },

    cvvChange() {
      this.$emit('inputChange', {
        name: 'cvv',
        value: this.formCvv,
      });
      this.$emit('change', { name: 'cvv', value: this.formCvv });
    },

    // 校验方法
    validate() {
      // 格式验证
      const cardNumRes = this.cardNumberRules(this.formCardNum);
      const effectiveDateRes = this.effectiveDateRules(this.formEffectiveDate);
      const cvvRes = this.cvvRules(this.formCvv);

      // 更新验证结果
      this.validator.cardNum = cardNumRes.result;
      this.validator.effectiveDate = effectiveDateRes.result;
      this.validator.cvv = cvvRes.result;

      // 按照优先级设置错误信息
      if (!cardNumRes.result) {
        this.errorMsg = cardNumRes.message;
      } else if (!effectiveDateRes.result) {
        this.errorMsg = effectiveDateRes.message;
      } else if (!cvvRes.result) {
        this.errorMsg = cvvRes.message;
      } else {
        this.errorMsg = '';
      }

      // 获取最终验证结果
      const isValid = this.getValidateResult();

      // 触发validate事件，只提供必要信息
      this.$emit('validate', {
        valid: isValid,
        allFieldsRequired: this.isFullValidation && this.allFieldsRequired,
        isCardInfo: true,
      });

      return isValid;
    },

    // 获取错误信息对象，供动态表单组件使用
    getErrors() {
      // 按照优先级顺序处理错误信息（卡号 > 有效期 > cvv）
      if ((this.isFullValidation && this.cardNumRequired) || !this.validator.cardNum) {
        // 卡号错误（最高优先级）
        return this.cardNumRequired
          ? this.$t('message.validator.required')
          : this.$t('message.validator.input', { field: this.$t('message.checkout.cardNumber').toLowerCase() });
      }

      if ((this.isFullValidation && this.effectiveDateRequired) || !this.validator.effectiveDate) {
        // 有效期错误（第二优先级）
        return this.effectiveDateRequired
          ? this.$t('message.validator.required')
          : this.$t('message.validator.input', { field: this.$t('message.checkout.expiredDate').toLowerCase() });
      }

      if ((this.isFullValidation && this.cvvRequired) || !this.validator.cvv) {
        // CVV错误（最低优先级）
        return this.cvvRequired
          ? this.$t('message.validator.required')
          : this.$t('message.validator.input', { field: this.$t('message.checkout.cvv').toLowerCase() });
      }

      return '';
    },

    // 获取校验结果
    getValidateResult() {
      return !((this.isFullValidation && this.cardNumRequired) || !this.validator.cardNum) &&
        !((this.isFullValidation && this.effectiveDateRequired) || !this.validator.effectiveDate) &&
        !((this.isFullValidation && this.cvvRequired) || !this.validator.cvv);
    },

    // 设置验证模式，与动态表单保持一致
    setValidationMode(mode) {
      if (['format-only', 'full'].includes(mode)) {
        this.internalValidationMode = mode;
        return true;
      }

      return false;
    },

    // 动态展示银行卡图片
    showCardImage() {
      let formattedNumber = this.formCardNum.replace(/\D/g, '');
      formattedNumber = formattedNumber.replace(/(.{4})/g, '$1 ').trim();
      this.formCardNum = formattedNumber;
      this.$emit('update:value', this.formCardNum);

      this.card = getCard(this.formCardNum);
      if (!this.card) {
        this.card = { logoPath: require('@/assets/images/cards/Default.png') };
      }
    },
    effectiveDateChangeHandler(evt) {
      // 获取当前值并移除非数字和斜杠
      let value = evt.target.value.replace(/[^\d/]/g, '');

      // 移除所有空格和斜杠，只保留数字
      let numbers = value.replace(/[\s/]/g, '');

      // 如果没有数字，直接清空
      if (!numbers) {
        this.formEffectiveDate = '';
        return;
      }

      // 处理月份
      if (numbers.length >= 2) {
        const month = parseInt(numbers.substring(0, 2), 10);
        // 如果月份为0或大于12，则设置为01
        if (month === 0 || month > 12) {
          numbers = '01' + numbers.substring(2);
        }
      }

      // 根据数字长度格式化
      if (numbers.length >= 2) {
        value = numbers.substring(0, 2);
        if (numbers.length > 2) {
          value += ' / ' + numbers.substring(2);
        }
      } else {
        value = numbers;
      }

      // 限制年份长度
      if (numbers.length > 4) {
        value = value.substring(0, 7);
      }

      // 更新输入框值
      this.formEffectiveDate = value;
    },
    cvvChangeHandler(event) {
      const value = event.target.value.replace(/[^\d]/g, '');
      this.formCvv = value;
      // this.formCvv = this.formCvv.replace(/[^\d]/g, '');
      // 移除在input时的验证
    },
  },

  watch: {
    formCardNum: {
      handler(newVal) {
        this.showCardImage(newVal);
      },
      immediate: true,
    },
    // 监听验证模式变更
    validationMode: {
      handler(newMode) {
        this.internalValidationMode = newMode;
      },
      immediate: true,
    },

    // 监听props变化，确保双向同步
    cardNum: {
      handler(val) {
        if (val !== this.formCardNum) {
          this.formCardNum = val;
        }
      },
      immediate: true,
    },

    // 监听effectiveDate属性变化
    effectiveDate: {
      handler(val) {
        if (val !== this.formEffectiveDate) {
          this.formEffectiveDate = val;
        }
      },
      immediate: true,
    },

    // 监听cvv属性变化
    cvv: {
      handler(val) {
        if (val !== this.formCvv) {
          this.formCvv = val;
        }
      },
      immediate: true,
    },
  },
};
</script>

<style lang="scss" scoped>
$color-primary: #503DF6;
@import '@/styles/variables.scss';

.card-info-suffix {

  .card-info-suffix-content {
    display: flex;
    align-items: center;
    justify-content: end;
    gap: 4px;

    img {
      width: 32px;
      height: auto;
    }
  }
}

.card-info-suffix-card {
  display: flex;
  align-items: center;
}

.img-cvv-box {
  display: flex;
  height: 100%;
  align-items: center;
}

.icon-cvv {
  width: 32px;
  height: 25px;
  transform: translateY(-3px);
}

.fixed-item {
  width: 32px;
  height: auto;
}

.fade-carousel {
  position: relative;
  width: 32px;
  height: auto;
  bottom: 11px;
}

.fade-item {
  position: absolute;
  top: 0;
  left: 0;
  width: 30px;
  height: 16px;
  opacity: 0;
  transition: opacity 1s ease;
}

.fade-item.active {
  opacity: 1;
}

.card-info-under-group {
  display: flex;
  align-items: center;
  margin-top: -1px;
  position: relative;
}

.card-info-input {
  ::v-deep .on-input {
    border-radius: 8px 8px 0 0;
    &:focus {
      position: relative;
      z-index: 10;
    }

    &.error {
      position: relative;
      z-index: 10;
    }
  }
}

.effectiveDate-input {
  ::v-deep .on-input {
    border-radius: 0px 0px 0 8px;

    &:focus {
      position: relative;
      z-index: 10;
    }

    &.error {
      position: relative;
      z-index: 10;
    }
  }
}

.cvv-input {
  ::v-deep .on-input {
    border-radius: 0px 0px 8px 0;
    margin-left: -1px;
    width: calc(100% + 1px);

    &:focus {
      position: relative;
      z-index: 10;
    }

    &.error {
      position: relative;
      z-index: 10;
    }
  }
}

.field-error {
  font-size: 12px;
  color: $color-form-error;
  font-family: Open Sans regular;

  &.bottom-left {
    display: block;
    margin-top: 6px;
    margin-bottom: 0;
    min-height: 0;
    text-align: left;
  }
}

// Vue Transition动画
.slide-bottom-enter-active {
  animation: slideDown 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
}

.slide-bottom-leave-active {
  animation: slideUp 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
}

// 滑入动画 - 从上往下
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-15px);
    max-height: 0;
  }
  to {
    opacity: 1;
    transform: translateY(0);
    max-height: 50px;
  }
}

// 滑出动画 - 从下往上
@keyframes slideUp {
  from {
    opacity: 1;
    transform: translateY(0);
    max-height: 50px;
  }
  to {
    opacity: 0;
    transform: translateY(-15px);
    max-height: 0;
  }
}
</style>
